// Utilidades del Gestor de exámenes (ReviewTests, ListadoGrupal, ListadoAlumnos).
//
// Viven aquí y no en un util general a propósito: el resto de las pantallas ya
// filtran con `includes` crudo y no se tocan.

/** Quita acentos y mayúsculas para que "matematicas" encuentre "Matemáticas". */
export const normalizarTexto = (texto) =>
  String(texto ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

/** Búsqueda tolerante: sin acentos, sin mayúsculas y sin espacios de sobra. */
export const coincideBusqueda = (texto, busqueda) => {
  const aguja = normalizarTexto(busqueda).trim();
  if (!aguja) return true;
  return normalizarTexto(texto).includes(aguja);
};

/** Nombre completo del alumno, sin los "undefined" que deja concatenar a ciegas. */
export const nombreAlumno = (alumno) =>
  [
    alumno?.usuario?.nombres,
    alumno?.usuario?.primerApellido,
    alumno?.usuario?.segundoApellido,
  ]
    .filter(Boolean)
    .join(" ")
    .trim();

/**
 * Estado de un alumno frente a un examen.
 *
 * `esActivo` significa que el examen se inició y no se ha finalizado: el alumno
 * lo tiene abierto o se le acabó el tiempo sin cerrarlo. No es lo mismo que "sin
 * presentar" y conviene distinguirlo, porque es el caso en el que el profesor
 * tiene que intervenir.
 */
export const estadoAlumno = (calificacion) => {
  if (!calificacion) return "SIN_PRESENTAR";
  if (calificacion.esActivo) return "EN_CURSO";
  return "TERMINADO";
};

export const ETIQUETA_ESTADO = {
  SIN_PRESENTAR: { texto: "Sin presentar", clases: "bg-gray-100 text-gray-500" },
  EN_CURSO: { texto: "En curso", clases: "bg-amber-100 text-amber-700" },
  TERMINADO: { texto: "Terminado", clases: "bg-green-100 text-green-700" },
};

/** La calificación puede ser 0, así que no sirve el `? :` sobre el valor. */
export const formatearCalificacion = (calificacion) =>
  calificacion === null || calificacion === undefined || calificacion === ""
    ? "—"
    : String(Math.round(Number(calificacion) * 100) / 100);

/**
 * Resumen de un examen dentro de un grupo: cuántos lo terminaron, cuántos lo
 * tienen abierto y el promedio de los que ya tienen calificación.
 *
 * Se calcula en el cliente con las calificaciones del grupo que ya se piden una
 * sola vez; no hay endpoint que lo devuelva agregado y pedir uno por examen
 * serían N peticiones para pintar una tabla.
 */
export const resumenExamen = (calificacionesGrupo, examenId, totalAlumnos) => {
  const delExamen = (calificacionesGrupo || []).filter(
    (calificacion) => String(calificacion?.uma) === String(examenId)
  );

  const terminados = delExamen.filter((calificacion) => !calificacion.esActivo);
  const enCurso = delExamen.filter((calificacion) => calificacion.esActivo);

  const conCalificacion = terminados.filter(
    (calificacion) =>
      calificacion.calificacion !== null && calificacion.calificacion !== undefined
  );

  const promedio =
    conCalificacion.length > 0
      ? conCalificacion.reduce((suma, el) => suma + Number(el.calificacion), 0) /
        conCalificacion.length
      : null;

  return {
    terminados: terminados.length,
    enCurso: enCurso.length,
    total: totalAlumnos ?? 0,
    promedio,
    porcentaje:
      totalAlumnos > 0 ? Math.round((terminados.length / totalAlumnos) * 100) : 0,
  };
};

/**
 * Resumen de una tarea dentro de un grupo.
 *
 * A diferencia de los exámenes, aquí no hay un endpoint que devuelva las
 * calificaciones de todo el grupo de una sola vez: `getCalificacionesGrupales`
 * de tareas es por tarea. El llamador decide cómo las pide.
 *
 * Un alumno sin registro cuenta como PENDIENTE: el registro se crea cuando
 * entrega, así que "no hay fila" y "no entregó" son lo mismo.
 */
export const resumenTarea = (calificaciones, totalAlumnos) => {
  const registros = calificaciones || [];

  const entregadas = registros.filter(
    (registro) => registro?.estatus && registro.estatus !== "PENDIENTE"
  );
  const porCalificar = registros.filter(
    (registro) => registro?.estatus === "ENVIADA"
  );
  const calificadas = registros.filter(
    (registro) => registro?.estatus === "CALIFICADA"
  );

  const conCalificacion = calificadas.filter(
    (registro) => typeof registro?.calificacionFinal === "number"
  );

  const promedio =
    conCalificacion.length > 0
      ? conCalificacion.reduce(
          (suma, registro) => suma + Number(registro.calificacionFinal),
          0
        ) / conCalificacion.length
      : null;

  return {
    entregadas: entregadas.length,
    porCalificar: porCalificar.length,
    calificadas: calificadas.length,
    total: totalAlumnos ?? 0,
    promedio,
    porcentaje:
      totalAlumnos > 0
        ? Math.round((entregadas.length / totalAlumnos) * 100)
        : 0,
  };
};
